/**
 * @fileoverview Utility for filtering and sorting visible menu items.
 *
 * Exports a function to determine which menu items should be shown based on
 * authentication status and feature flags, and sorts them by their order property.
 */

import { AppLinkTypes } from '@app/config/appLink'
import type { FeatureFlagKey } from '@app/types'

/**
 * Returns an array of menu items that should be visible to the user.
 *
 * Filters menu items based on their `showInMenu`, `requiresAuth`, and `featureFlag` properties,
 * and sorts them by their `order` property.
 *
 * @param {Record<string, AppLinkTypes>} appLink - The map of all possible menu items.
 * @param {boolean} isLoggedIn - Whether the user is authenticated.
 * @param {Record<FeatureFlagKey, boolean>} featureFlagMap - Map of feature flags and their enabled state.
 * @returns {AppLinkTypes[]} Array of visible and sorted menu items.
 */

// TODO: Now all the feature flags are coming from API only if it's enable.

export function getVisibleMenuItems(
  appLink: Record<string, AppLinkTypes>,
  isLoggedIn: boolean,
  featureFlagMap: Record<FeatureFlagKey, boolean>,
): AppLinkTypes[] {
  return Object.values(appLink)
    .filter((item) => {
      const isVisible = item.showInMenu === true
      const requiresAuth = item.requiresAuth ?? true
      const isAuthOk = isLoggedIn || requiresAuth === false

      const featureKey = item.featureFlag as FeatureFlagKey | undefined
      const featureFlagReverse = item.featureFlagReverse ?? false
      const isFeatureOk = !featureKey || featureFlagMap[featureKey] === true

      // Note re feature flags: if featureFlagReverse is true, we want to hide the
      // item when the flag is enabled. `featureFlagReverse != isFeatureOk` achieves this.s
      return isVisible && isAuthOk && featureFlagReverse != isFeatureOk
    })
    .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
}
